import { ref, getCurrentInstance } from 'vue'
import apiUseCase from '@/usecase/apiUseCase'
import returnUrlPrams from '@/utils/helpers/returnUrlPrams'
import { IFuelingTransaction, IFuelingTransactionDetail, IFuelingTransactionHistoryItem } from '@/utils/interfaces/iFuelMonitoring'

export function useFuelTransactionDetail() {
  const app = getCurrentInstance()
  const { $toast } = app ? app.appContext.config.globalProperties : { $toast: null } as any

  // State
  const isDetailOpen = ref(false)
  const isLoadingDetail = ref(false)
  const isLoadingHistory = ref(false)
  const transactionDetail = ref<IFuelingTransactionDetail | null>(null)
  const historyData = ref<IFuelingTransactionHistoryItem[]>([])
  const activeHistoryTab = ref<'nopol' | 'kru'>('nopol')

  const historyPaginate = ref({
    pageSize: 10,
    pageNumber: 1,
    totalRecords: 0
  })

  // Fetch transaction details
  const fetchDetail = async (id: string) => {
    isLoadingDetail.value = true
    try {
      const response = await apiUseCase.get(`/transaction/v2/FuelingTransactions/${id}`)
      if (!response.error) {
        transactionDetail.value = response.result
      } else if ($toast) {
        $toast.add({ severity: 'error', detail: response.message, group: 'bc', life: 3000 })
      }
    } catch (err) {
      console.error(err)
    } finally {
      isLoadingDetail.value = false
    }
  }

  // Fetch fueling history
  const fetchHistory = async () => {
    if (!transactionDetail.value) return
    isLoadingHistory.value = true

    const customFilters: string[][] = []
    if (activeHistoryTab.value === 'nopol') {
      const vehicleId = transactionDetail.value.VehicleId
      if (vehicleId) {
        customFilters.push(['vehicleId', '=', vehicleId])
      }
    } else {
      const crewId = transactionDetail.value.CrewId
      if (crewId) {
        customFilters.push(['crewId', '=', crewId])
      }
    }

    const queryObj = {
      custom: customFilters,
      pageSize: historyPaginate.value.pageSize,
      pageNumber: historyPaginate.value.pageNumber
    }

    const queryStr = returnUrlPrams(queryObj)
    try {
      const response = await apiUseCase.get('/transaction/v2/FuelingTransactions', queryStr)
      if (!response.error) {
        historyData.value = response.result || []
        historyPaginate.value.totalRecords = response.count || 0
      } else if ($toast) {
        $toast.add({ severity: 'error', detail: response.message, group: 'bc', life: 3000 })
      }
    } catch (err) {
      console.error(err)
    } finally {
      isLoadingHistory.value = false
    }
  }

  // Open detail popup
  const openDetail = async (item: IFuelingTransaction) => {
    isDetailOpen.value = true
    activeHistoryTab.value = 'nopol'
    historyPaginate.value.pageNumber = 1
    historyData.value = []

    await fetchDetail(item.Id)
    await fetchHistory()
  }

  // Close detail popup
  const closeDetail = () => {
    isDetailOpen.value = false
    transactionDetail.value = null
    historyData.value = []
  }

  const onChangeHistoryTab = async (tab: 'nopol' | 'kru') => {
    activeHistoryTab.value = tab
    historyPaginate.value.pageNumber = 1
    await fetchHistory()
  }

  const onChangeHistoryPage = async (event: any) => {
    historyPaginate.value.pageNumber = event.page + 1
    await fetchHistory()
  }

  return {
    isDetailOpen,
    isLoadingDetail,
    isLoadingHistory,
    transactionDetail,
    historyData,
    activeHistoryTab,
    historyPaginate,
    openDetail,
    closeDetail,
    onChangeHistoryTab,
    onChangeHistoryPage,
    fetchHistory,
    $toast
  }
}
